// If you want to use Phoenix channels, run `mix help phx.gen.channel`
// to get started and then uncomment the line below.
// import "./user_socket.js"

// You can include dependencies in two ways.
//
// The simplest option is to put them in assets/vendor and
// import them using relative paths:
//
//     import "../vendor/some-package.js"
//
// Alternatively, you can `npm install some-package --prefix assets` and import
// them using a path starting with the package name:
//
//     import "some-package"
//
// If you have dependencies that try to import CSS, esbuild will generate a separate `app.css` file.
// To load it, simply add a second `<link>` to your `root.html.heex` file.

// Sentry first, so its global handlers catch errors thrown by everything
// that loads after it (no-op unless the layout rendered a sentry-dsn meta).
import { addBreadcrumb } from './sentry'
// Include phoenix_html to handle method=PUT/DELETE in forms and buttons.
import 'phoenix_html'
// Establish Phoenix Socket and LiveView configuration.
import {Socket} from 'phoenix'
import {LiveSocket} from 'phoenix_live_view'
import {hooks as colocatedHooks} from 'phoenix-colocated/brainbox'
import topbar from '../vendor/topbar'
import Hooks from './hooks'
import BrainboxPort from './lib/brainbox_port'
import { openInWavebox } from './lib/wavebox_link_opener'
import { getCredentials, hasCredentials, pinCredentials, recordCredentialsSent, setCredentials } from './lib/auth_state'
import { reconnectAfterMs } from './lib/reconnect_backoff.mjs'
import { joinParamsOnly } from './lib/join_params_socket.mjs'

// Global click interceptor: any anchor marked with data-wavebox-link has its
// click routed through the extension so the resulting tab lands in the user's
// current Space/partition. Anchors additionally marked with
// data-wavebox-elevate also request that the side panel be promoted to
// window scope first (same dedup semantics as the first-tool-call path) —
// used for deliberate user navigations like "Further reading" chips where
// losing the side panel on tab navigation would be disruptive.
// Middle-click, modifier-click, and non-primary button clicks are left
// alone so the browser's native behaviour (open in background tab, etc.)
// still works.
document.addEventListener('click', (e) => {
  const anchor = e.target?.closest?.('a[data-wavebox-link]')
  if (!anchor) {return}
  const href = anchor.getAttribute('href')
  if (!href) {return}
  if (e.button !== 0) {return}
  if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) {return}
  e.preventDefault()
  const elevate = anchor.getAttribute('data-wavebox-elevate') === 'true'
  openInWavebox(href, { elevate })
}, false)

// Seed the credentials from URL params, for the dev/test bypass. A complete
// pair here pins the identity, so opening Brainbox as a chosen user still works
// inside Wavebox; otherwise the extension supplies them once its port connects.
const urlParams = new URLSearchParams(window.location.search)
pinCredentials({
  client_id: urlParams.get('client_id'),
  client_token: urlParams.get('client_token')
})

const safeStringify = (value) => {
  if (value === undefined) { return undefined }
  try {
    return typeof value === 'string' ? value : JSON.stringify(value)
  } catch {
    return String(value)
  }
}

// The credentials travel in each join's payload, never the socket URL
// (see join_params_socket.mjs)
const liveSocket = new LiveSocket('/live', joinParamsOnly(Socket), {
  longPollFallbackMs: 2500,
  // Jittered, so a deploy's reconnects spread out (see reconnect_backoff.mjs).
  reconnectAfterMs,
  hooks: {...colocatedHooks, ...Hooks},
  params: () => {
    // Evaluated on every channel join/rejoin — `get_connect_params/1` reads
    // the join payload, so an auto-rejoin after a channel error re-sends
    // these without any socket cycle. This is the only
    // channel by which the server ever sees the credentials (BrainboxLive
    // validates from connect params alone; it consults no session). Recording
    // what goes out lets the ExtensionBridge hook tell a genuine credential
    // change from a repeat of the pair the connection already carries.
    recordCredentialsSent()
    const credentials = getCredentials()
    // eslint-disable-next-line no-console -- Authentication diagnostics log presence flags, never credentials.
    console.log('[Brainbox] LiveSocket params requested:', {
      hasClientId: !!credentials.client_id,
      hasClientToken: !!credentials.client_token
    })
    return credentials
  }
})

// LiveView routes its own "unable to join" and "view crashed" messages through
// `LiveSocket.log`, which discards them unless debug is enabled. Neither of
// those failures throws, so sentry.js never sees them either: without this, a
// view that fails to join shows the user a reconnect banner and leaves no trace
// anywhere at all.
//
// Wrapping `log` rather than passing the `viewLogger` option, which would look
// tidier but is a performance trap: supplying a viewLogger makes LiveView
// invoke the message thunk for EVERY log kind, and `View.applyDiff` passes
// `() => ["", clone(rawDiff)]` — a JSON.parse(JSON.stringify(...)) of every
// server diff, on every frame, in production, computed and thrown away unread.
// Delegating the non-error kinds keeps them behind the original
// `isDebugEnabled()` short-circuit (and keeps LiveView's own richer dev output).
//
// Breadcrumb, deliberately not captureMessage: `onError` fires for every
// connected client on a server restart or deploy, so these would flood as
// issues. As breadcrumbs they cost nothing and give whatever error DOES get
// reported the context that the view had dropped moments earlier.
const originalLog = liveSocket.log.bind(liveSocket)
liveSocket.log = (view, kind, msgCallback) => {
  if (kind !== 'error') { return originalLog(view, kind, msgCallback) }

  const [msg, obj] = msgCallback()
  const context = {
    viewId: view?.id,
    joinCount: view?.joinCount,
    socketConnected: liveSocket.isConnected()
  }
  console.error(`[Brainbox] LiveView error: ${msg}`, obj, context)
  addBreadcrumb({
    category: 'liveview',
    level: 'error',
    message: `LiveView error: ${msg}`,
    // `obj` is LiveView's raw reason — a join-error reply or a crash reason.
    // Stringified because it is not guaranteed to be serialisable.
    data: {...context, reason: safeStringify(obj)}
  })
}

// Show progress bar on live navigation and form submits
topbar.config({barColors: {0: '#8b5cf6'}, shadowColor: 'rgba(0, 0, 0, .3)'})
window.addEventListener('phx:page-loading-start', () => topbar.show(300))
window.addEventListener('phx:page-loading-stop', () => topbar.hide())

const bootstrapErrorTarget = document.getElementById('brainbox-bootstrap-error')
const extensionBridgeEl = document.getElementById('extension-bridge')

const showBootstrapError = (message) => {
  if (bootstrapErrorTarget) {
    const messageEl = bootstrapErrorTarget.querySelector('[data-error-message]')
    if (messageEl) {
      messageEl.textContent = message
    }
    bootstrapErrorTarget.classList.remove('hidden')
  } else {
    console.error('[Brainbox] Authentication error:', message)
  }
}

const waitForExtensionCredentials = () => {
  return new Promise((resolve, reject) => {
    if (!extensionBridgeEl) {
      reject(new Error('Wavebox extension metadata not found'))
      return
    }

    const extensionId = extensionBridgeEl.dataset.extensionId
    const timeoutMs = parseInt(extensionBridgeEl.dataset.connectionTimeout || '5000', 10)

    if (!extensionId) {
      reject(new Error('Missing Wavebox extension ID'))
      return
    }

    if (!window.chrome?.runtime?.connect) {
      reject(new Error('Brainbox must be opened from inside Wavebox'))
      return
    }

    let timeoutRef = null

    const cleanup = () => {
      if (timeoutRef) {
        clearTimeout(timeoutRef)
      }
      BrainboxPort.off('port-connected', handleConnected)
      BrainboxPort.off('port-disconnected', handleDisconnected)
    }

    const handleConnected = (authData) => {
      cleanup()
      // eslint-disable-next-line no-console -- Authentication diagnostics log presence flags, never credentials.
      console.log('[Brainbox] Extension connected with auth data:', {
        hasClientId: !!authData?.client_id,
        hasClientToken: !!authData?.client_token
      })
      if (authData?.client_id && authData?.client_token) {
        setCredentials(authData)
        window.__brainboxAuth = authData
        // eslint-disable-next-line no-console -- Trace socket bootstrap progress for connection diagnostics.
        console.log('[Brainbox] Auth state updated, ready to connect LiveSocket')
        resolve(authData)
      } else {
        reject(new Error('Extension returned incomplete credentials'))
      }
    }

    const handleDisconnected = () => {
      cleanup()
      reject(new Error('Lost connection to Wavebox extension'))
    }

    BrainboxPort.on('port-connected', handleConnected)
    BrainboxPort.on('port-disconnected', handleDisconnected)

    try {
      BrainboxPort.connect(extensionId)
    } catch (error) {
      cleanup()
      reject(error)
      return
    }

    timeoutRef = setTimeout(() => {
      cleanup()
      reject(new Error('Extension did not respond in time'))
    }, timeoutMs)
  })
}

const connectLiveSocket = async () => {
  try {
    // eslint-disable-next-line no-console -- Trace socket bootstrap progress for connection diagnostics.
    console.log('[Brainbox] Waiting for extension credentials...')
    await waitForExtensionCredentials()
    // eslint-disable-next-line no-console -- Trace socket bootstrap progress for connection diagnostics.
    console.log('[Brainbox] Credentials received, connecting LiveSocket...')
    liveSocket.connect()
    // eslint-disable-next-line no-console -- Trace socket bootstrap progress for connection diagnostics.
    console.log('[Brainbox] LiveSocket.connect() called')
  } catch (error) {
    console.error('[Brainbox] Failed to connect:', error)
    showBootstrapError(error.message)
  }
}

// The socket carries the credentials as connect params and the server has no
// session to fall back on, so connecting before they arrive would mount an
// unauthenticated LiveView that then has to be torn down and rebuilt. Inside
// Wavebox we therefore wait for the extension port first.
if (hasCredentials()) {
  // URL params provided - skip extension, connect directly (for dev/test)
  // eslint-disable-next-line no-console -- Identify the development authentication path in connection diagnostics.
  console.log('[Brainbox] Using URL param credentials, connecting directly')
  liveSocket.connect()
} else if (extensionBridgeEl) {
  // Await the extension credentials, then connect once, already authenticated
  connectLiveSocket()
} else {
  // Fallback to immediate connection when extension metadata is missing (tests/dev tools)
  // eslint-disable-next-line no-console -- Identify the test/development fallback in connection diagnostics.
  console.log('[Brainbox] No extension bridge element found, connecting anyway')
  liveSocket.connect()
}

// expose liveSocket on window for web console debug logs and latency simulation:
// >> liveSocket.enableDebug()
// >> liveSocket.enableLatencySim(1000)  // enabled for duration of browser session
// >> liveSocket.disableLatencySim()
window.liveSocket = liveSocket

// Notify parent window when running in iframe and auth state is determined
// Only notify once we have a definitive state (not :setup)
if (window !== window.parent) {
  // Get extension ID from the extension bridge element
  const extensionBridge = document.getElementById('extension-bridge')
  const extensionId = extensionBridge?.dataset?.extensionId
  const targetOrigin = extensionId ? `chrome-extension://${extensionId}` : '*'

  const notifyParent = (authState) => {
    // Only notify for definitive states: authorized, unauthorized, or error
    if (authState === 'authorized' || authState === 'unauthorized' || authState === 'error') {
      window.parent.postMessage({
        type: 'brainbox5-loaded',
        authState: authState
      }, targetOrigin) // Use specific origin instead of '*'
    }
  }

  // Listen for auth state changes from LiveView (server push_event)
  // This listener removes itself after first notification
  const handler = (e) => {
    notifyParent(e.detail.state)
    window.removeEventListener('phx:brainbox:auth-state-changed', handler)
  }
  window.addEventListener('phx:brainbox:auth-state-changed', handler)
}

// The lines below enable quality of life phoenix_live_reload
// development features:
//
//     1. stream server logs to the browser console
//     2. click on elements to jump to their definitions in your code editor
//
if (process.env.NODE_ENV === 'development') {
  window.addEventListener('phx:live_reload:attached', ({detail: reloader}) => {
    // Enable server log streaming to client.
    // Disable with reloader.disableServerLogs()
    reloader.enableServerLogs()

    // Open configured PLUG_EDITOR at file:line of the clicked element's HEEx component
    //
    //   * click with "c" key pressed to open at caller location
    //   * click with "d" key pressed to open at function component definition location
    let keyDown
    window.addEventListener('keydown', (e) => keyDown = e.key)
    window.addEventListener('keyup', () => keyDown = null)
    window.addEventListener('click', (e) => {
      if(keyDown === 'c'){
        e.preventDefault()
        e.stopImmediatePropagation()
        reloader.openEditorAtCaller(e.target)
      } else if(keyDown === 'd'){
        e.preventDefault()
        e.stopImmediatePropagation()
        reloader.openEditorAtDef(e.target)
      }
    }, true)

    window.liveReloader = reloader
  })
}
